import { useState } from "react";
import { FlaskConical, Calendar } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SeederPanel } from "@/components/pipeline-validator/SeederPanel";
import { AuditDashboard } from "@/components/pipeline-validator/AuditDashboard";
import { PipelineRunner } from "@/components/pipeline-validator/PipelineRunner";

const MONTHS = [
	"January",
	"February",
	"March",
	"April",
	"May",
	"June",
	"July",
	"August",
	"September",
	"October",
	"November",
	"December",
];

export function PipelineValidatorPage() {
	const now = new Date();
	const [organizationId, setOrganizationId] = useState("");
	const [month, setMonth] = useState(now.getMonth() + 1);
	const [year, setYear] = useState(now.getFullYear());

	const periodStart = `${year}-${String(month).padStart(2, "0")}-01`;
	const lastDay = new Date(year, month, 0).getDate();
	const periodEnd = `${year}-${String(month).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;

	return (
		<div className="space-y-6 p-6">
			<div className="flex items-center gap-3">
				<FlaskConical className="h-6 w-6" />
				<h1 className="text-2xl font-bold">Pipeline Validator</h1>
			</div>

			<Card>
				<CardHeader>
					<CardTitle className="flex items-center gap-2">
						<Calendar className="h-5 w-5" />
						Configuration
					</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
						<div className="space-y-2">
							<Label htmlFor="org-id">Organization ID</Label>
							<Input
								id="org-id"
								placeholder="Leave empty to create sandbox org"
								value={organizationId}
								onChange={(e) =>
									setOrganizationId(e.target.value)
								}
							/>
							<p className="text-xs text-muted-foreground">
								Leave empty to create a new sandbox org when seeding
							</p>
						</div>
						<div className="space-y-2">
							<Label htmlFor="month-select">Month</Label>
							<select
								id="month-select"
								className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
								value={month}
								onChange={(e) =>
									setMonth(Number(e.target.value))
								}
							>
								{MONTHS.map((name, idx) => (
									<option key={name} value={idx + 1}>
										{name}
									</option>
								))}
							</select>
						</div>
						<div className="space-y-2">
							<Label htmlFor="year-input">Year</Label>
							<Input
								id="year-input"
								type="number"
								min={2020}
								max={2030}
								value={year}
								onChange={(e) =>
									setYear(Number(e.target.value))
								}
							/>
						</div>
					</div>
				</CardContent>
			</Card>

			<Tabs defaultValue="seed">
				<TabsList>
					<TabsTrigger value="seed">Seed Data</TabsTrigger>
					<TabsTrigger value="audit">Audit Dashboard</TabsTrigger>
					<TabsTrigger value="runner">Pipeline Runner</TabsTrigger>
				</TabsList>

				<TabsContent value="seed" className="mt-4">
					<SeederPanel
						organizationId={organizationId}
						year={year}
						month={month}
						onOrgCreated={(orgId) => setOrganizationId(orgId)}
					/>
				</TabsContent>

				<TabsContent value="audit" className="mt-4">
					<AuditDashboard
						organizationId={organizationId}
						periodStart={periodStart}
						periodEnd={periodEnd}
					/>
				</TabsContent>

				<TabsContent value="runner" className="mt-4">
					<PipelineRunner
						organizationId={organizationId}
						periodStart={periodStart}
						periodEnd={periodEnd}
					/>
				</TabsContent>
			</Tabs>
		</div>
	);
}
